import * as React from "react";
import { Button, FormGroup, Input, Label, Modal } from "../../components/Modal";
import SquareButton from "../../general/SquareButton";
import { AppWebsiteConfiguration, AppWebsiteScreenshot, CreateManagedService, ManagedService, ManagedServiceType, UpdateManagedService, UploadAppWebsiteImage } from "../ManagedServiceStore";

const emptyConfiguration = (): AppWebsiteConfiguration => ({
    domain: "", aliases: [], published: false, appName: "", school: "", shortName: "", tagline: "",
    schoolWebsite: "", supportEmail: "support@scadco.co.za", androidPackage: "", iosBundleId: "", multiSchool: false,
    themePrefix: "scadsys-portal-app-", logoImageKey: "applogo", iconImageKey: "appicon",
    logoUrl: "", iconUrl: "", faviconUrl: "", palette: { primary: "", secondary: "", deep: "" },
    stores: { android: { status: "soon", url: "" }, ios: { status: "soon", url: "" } }, screenshots: [], heroScreenshot: 0,
});

function errorMessages(error: any): string[] {
    const data = error?.response?.data;
    if (Array.isArray(data?.errors)) return data.errors.map((e: any) => e.message || String(e));
    return [typeof data === "string" ? data : data?.message || error?.message || "Unable to save the website."];
}

interface EditorProps {
    organizationId: string;
    managedService?: ManagedService<AppWebsiteConfiguration>;
    onDone: () => void;
}

export const AppWebsiteEditor = ({ organizationId, managedService, onDone }: EditorProps) => {
    const initial = () => ({ ...emptyConfiguration(), ...managedService?.configuration });
    const [config, setConfig] = React.useState<AppWebsiteConfiguration>(initial);
    const [aliases, setAliases] = React.useState((managedService?.configuration.aliases || []).join(String.fromCharCode(10)));
    const [errors, setErrors] = React.useState<string[]>([]);
    const [busy, setBusy] = React.useState(false);
    React.useEffect(() => {
        setConfig(initial());
        setAliases((managedService?.configuration.aliases || []).join(String.fromCharCode(10)));
        setErrors([]);
    }, [managedService?.id]);

    const textField = (key: keyof AppWebsiteConfiguration, label: string, placeholder = "") => (
        <FormGroup key={key}>
            <Label htmlFor={"website-" + key}>{label}</Label>
            <Input id={"website-" + key} type="text" value={String(config[key] ?? "")} placeholder={placeholder}
                disabled={busy} onChange={(e) => setConfig(c => ({ ...c, [key]: e.target.value }))} />
        </FormGroup>
    );
    const updateShot = (index: number, values: Partial<AppWebsiteScreenshot>) => setConfig(c => ({
        ...c, screenshots: c.screenshots.map((s, i) => i === index ? { ...s, ...values } : s),
    }));
    const moveShot = (index: number, offset: number) => setConfig(c => {
        const next = index + offset;
        if (next < 0 || next >= c.screenshots.length) return c;
        const screenshots = [...c.screenshots];
        [screenshots[index], screenshots[next]] = [screenshots[next], screenshots[index]];
        const heroScreenshot = c.heroScreenshot === index ? next : c.heroScreenshot === next ? index : c.heroScreenshot;
        return { ...c, screenshots, heroScreenshot };
    });
    const upload = async (file: File | undefined, apply: (url: string) => void) => {
        if (!file || !managedService) return;
        if (file.size > 8 * 1024 * 1024) { setErrors(["Choose an image up to 8 MB."]); return; }
        setBusy(true); setErrors([]);
        try { apply((await UploadAppWebsiteImage(organizationId, managedService.id, file)).data.url); }
        catch (error) { setErrors(errorMessages(error)); }
        finally { setBusy(false); }
    };
    const uploadField = (label: string, apply: (url: string) => void) => managedService ? (
        <label className="block text-sm text-gray-700">{label}
            <input type="file" accept="image/png,image/jpeg,image/webp" disabled={busy} className="mt-1 block w-full text-sm"
                onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ""; upload(file, apply); }} />
        </label>
    ) : null;
    const save = async (event: React.FormEvent) => {
        event.preventDefault(); setBusy(true); setErrors([]);
        const configuration = { ...config, aliases: aliases.split(/[\n,]+/).map(s => s.trim()).filter(Boolean) };
        try {
            if (managedService) await UpdateManagedService(organizationId, { ...managedService, configuration });
            else await CreateManagedService(organizationId, { type: ManagedServiceType.AppWebsite, organizationID: organizationId, configuration });
            onDone();
        } catch (error) { setErrors(errorMessages(error)); }
        finally { setBusy(false); }
    };

    return <form onSubmit={save} className="space-y-6">
        <div>
            <h3 className="text-lg font-semibold">App Website</h3>
            <p className="text-sm text-gray-600">Configure the app landing page, download links and screenshots.</p>
        </div>
        {errors.length > 0 && <div role="alert" className="rounded border border-red-200 bg-red-50 p-3">
            {errors.map((message, i) => <p key={i} className="text-sm text-red-800">{message}</p>)}
        </div>}
        <fieldset disabled={busy} className="space-y-4">
            <legend className="font-semibold">Website address</legend>
            {textField("domain", "Primary hostname", "school.app.co.za")}
            <FormGroup><Label htmlFor="website-aliases">Other hostnames (one per line)</Label>
                <textarea id="website-aliases" value={aliases} onChange={e => setAliases(e.target.value)} className="w-full rounded border border-gray-300 p-2" rows={2} />
            </FormGroup>
            <label className="flex items-center gap-2"><input type="checkbox" checked={config.published} onChange={e => setConfig(c => ({ ...c, published: e.target.checked }))} />Publish website</label>
            <p className="text-xs text-gray-500">Unpublished websites are hidden. Saved changes appear on the website within its cache refresh interval.</p>
        </fieldset>
        <fieldset disabled={busy} className="space-y-4">
            <legend className="font-semibold">App details</legend>
            {textField("appName", "App name")}{textField("school", "School name")}{textField("shortName", "Short name")}
            {textField("tagline", "Tagline")}{textField("schoolWebsite", "School website", "https://")}
            {textField("supportEmail", "Support email")}
            <label className="flex items-center gap-2"><input type="checkbox" checked={config.multiSchool} onChange={e => setConfig(c => ({ ...c, multiSchool: e.target.checked }))} />App supports selecting multiple schools</label>
        </fieldset>
        <fieldset disabled={busy} className="space-y-4">
            <legend className="font-semibold">Download links</legend>
            {(["android", "ios"] as const).map(platform => <div key={platform} className="rounded border border-gray-200 p-3 space-y-2">
                <Label htmlFor={"website-store-" + platform}>{platform === "ios" ? "App Store" : "Google Play"}</Label>
                <select id={"website-store-" + platform} value={config.stores[platform].status} className="rounded border border-gray-300 p-2"
                    onChange={e => setConfig(c => ({ ...c, stores: { ...c.stores, [platform]: { ...c.stores[platform], status: e.target.value } } }))}>
                    <option value="soon">Coming soon</option><option value="live">Live</option>
                </select>
                <Input aria-label={(platform === "ios" ? "App Store" : "Google Play") + " URL"} type="url" value={config.stores[platform].url} placeholder="https://"
                    onChange={e => setConfig(c => ({ ...c, stores: { ...c.stores, [platform]: { ...c.stores[platform], url: e.target.value } } }))} />
            </div>)}
        </fieldset>
        <fieldset disabled={busy} className="space-y-4">
            <legend className="font-semibold">Branding</legend>
            <p className="text-sm text-gray-600">The page follows this organization's Identity branding. Leave overrides empty to keep it in sync.</p>
            {(["primary", "secondary", "deep"] as const).map(key => <FormGroup key={key}>
                <Label htmlFor={"website-colour-" + key}>{key === "deep" ? "Dark background colour" : key === "primary" ? "Primary colour" : "Secondary colour"}</Label>
                <Input id={"website-colour-" + key} value={config.palette[key]} placeholder="Follow Identity branding" onChange={e => setConfig(c => ({ ...c, palette: { ...c.palette, [key]: e.target.value } }))} />
            </FormGroup>)}
            {textField("logoUrl", "Logo URL override", "https://")}
            {uploadField("Upload a logo", url => setConfig(c => ({ ...c, logoUrl: url })))}
            {textField("iconUrl", "App icon URL override", "https://")}
            {uploadField("Upload an app icon", url => setConfig(c => ({ ...c, iconUrl: url })))}
            {textField("faviconUrl", "Favicon URL override", "Use the app icon")}
            {!managedService && <p className="text-sm text-gray-600">Create the website to upload images, or enter existing HTTPS image URLs.</p>}
            <details><summary className="cursor-pointer text-sm">Additional branding settings</summary><div className="mt-3 space-y-4">
                {textField("themePrefix", "Identity theme prefix")}{textField("logoImageKey", "Identity logo image key")}{textField("iconImageKey", "Identity app icon image key")}
                {textField("androidPackage", "Android package")}{textField("iosBundleId", "iOS bundle ID")}
            </div></details>
        </fieldset>
        <fieldset disabled={busy} className="space-y-4">
            <legend className="font-semibold">Screenshots</legend>
            <p className="text-sm text-gray-600">Use demo accounts in screenshots. Choose PNG, JPEG or WebP images up to 8 MB.</p>
            {config.screenshots.map((shot, index) => <div key={index} className="space-y-3 rounded border border-gray-200 p-3">
                <div className="flex flex-wrap items-center gap-3">
                    <b>Screenshot {index + 1}</b>
                    <label><input type="radio" name="website-hero" checked={config.heroScreenshot === index} onChange={() => setConfig(c => ({ ...c, heroScreenshot: index }))} /> Hero image</label>
                    <Button type="button" size="sm" disabled={index === 0} onClick={() => moveShot(index, -1)}>Move up</Button>
                    <Button type="button" size="sm" disabled={index === config.screenshots.length - 1} onClick={() => moveShot(index, 1)}>Move down</Button>
                    <Button type="button" size="sm" onClick={() => setConfig(c => ({ ...c, screenshots: c.screenshots.filter((_, i) => i !== index), heroScreenshot: c.heroScreenshot === index ? 0 : c.heroScreenshot > index ? c.heroScreenshot - 1 : c.heroScreenshot }))}>Remove</Button>
                </div>
                <Input aria-label={"Screenshot " + (index + 1) + " URL"} type="url" value={shot.url} placeholder="HTTPS image URL" onChange={e => updateShot(index, { url: e.target.value })} />
                {uploadField("Upload screenshot", url => updateShot(index, { url, imageKey: "" }))}
                <Input aria-label={"Screenshot " + (index + 1) + " title"} value={shot.title} placeholder="Title" onChange={e => updateShot(index, { title: e.target.value })} />
                <Input aria-label={"Screenshot " + (index + 1) + " caption"} value={shot.caption} placeholder="Caption" onChange={e => updateShot(index, { caption: e.target.value })} />
                <details><summary className="text-sm cursor-pointer">Use an existing Identity image</summary>
                    <Input aria-label={"Screenshot " + (index + 1) + " branding key"} value={shot.imageKey} placeholder="Branding image key" onChange={e => updateShot(index, { imageKey: e.target.value })} />
                </details>
            </div>)}
            <Button type="button" disabled={config.screenshots.length >= 24} onClick={() => setConfig(c => ({ ...c, screenshots: [...c.screenshots, { url: "", imageKey: "", title: "", caption: "" }] }))}>Add screenshot</Button>
        </fieldset>
        <div className="flex justify-end"><Button type="submit" variant="primary" disabled={busy}>{busy ? "Saving..." : managedService ? "Save website" : "Create website"}</Button></div>
    </form>;
};

export const AppWebsiteManagedService = ({ organizationId, onDone }: { organizationId: string; onDone: () => void }) => {
    const [open, setOpen] = React.useState(false);
    return <>
        <SquareButton className="col" onClick={() => setOpen(true)}>App Website</SquareButton>
        <Modal isOpen={open} onClose={() => setOpen(false)} title="New App Website Managed Service" size="xl">
            {open && <div className="p-6"><AppWebsiteEditor organizationId={organizationId} onDone={() => { setOpen(false); onDone(); }} /></div>}
        </Modal>
    </>;
};

export const RenderAppWebsiteRow = (config: AppWebsiteConfiguration) => <div>
    <div className="font-medium">{config.appName || "App Website"}</div>
    <div>{config.domain} — {config.published ? "Published" : "Unpublished"}</div>
</div>;
